import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Joyride, { CallBackProps, EVENTS, STATUS, Step } from "@/components/tour/Joyride";

interface TourStep extends Step {
  route?: string;
}

interface TourContextValue {
  isTourActive: boolean;
  isTourSkipped: boolean;
  startTour: () => void;
  endTour: () => void;
  skipTour: () => void;
  resetTour: () => void;
}

const TourContext = createContext<TourContextValue | undefined>(undefined);

const TOUR_STORAGE_KEY = "meethub.tour.skipped";

const TOUR_STEPS: TourStep[] = [
  {
    target: '[data-tour="create-session"]',
    title: "Welcome to MeetHub! 🎉",
    content: "Let's take a quick tour. Click this card to create your first meeting, then we'll show you key features in the meeting room.",
    placement: "bottom",
    route: "/dashboard",
  },
  {
    target: '[data-tour="add-notes"]',
    title: "Chat & Notes",
    content: "Use this to send messages and capture notes during your meeting. Great for questions and collaboration!",
    placement: "left",
    route: "/meeting/",
  },
  {
    target: '[data-tour="share-export"]',
    title: "Share Meeting",
    content: "Click here to copy the meeting code and share it with participants. You're all set! 🚀",
    placement: "left",
    route: "/meeting/",
  },
];

function getStoredSkipState() {
  return typeof window !== "undefined" && localStorage.getItem(TOUR_STORAGE_KEY) === "true";
}

interface ProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: ProviderProps) {
  const location = useLocation();
  const [stepIndex, setStepIndex] = useState(0);
  const [isTourActive, setIsTourActive] = useState(false);
  const [isTourSkipped, setIsTourSkipped] = useState(getStoredSkipState);
  const [hasStarted, setHasStarted] = useState(false);

  const currentStep = TOUR_STEPS[stepIndex];

  const isRouteMatch = useMemo(() => {
    if (!currentStep?.route) return true;
    return location.pathname.startsWith(currentStep.route);
  }, [currentStep?.route, location.pathname]);

  const startTour = useCallback(() => {
    if (isTourSkipped) return;
    setHasStarted(true);
    setIsTourActive(true);
    setStepIndex(0);
  }, [isTourSkipped]);

  const endTour = useCallback(() => {
    setIsTourActive(false);
    setHasStarted(false);
    setStepIndex(0);
  }, []);

  const skipTour = useCallback(() => {
    setIsTourSkipped(true);
    setIsTourActive(false);
    setHasStarted(false);
    if (typeof window !== "undefined") {
      localStorage.setItem(TOUR_STORAGE_KEY, "true");
    }
  }, []);

  const resetTour = useCallback(() => {
    setIsTourSkipped(false);
    setHasStarted(false);
    setStepIndex(0);
    if (typeof window !== "undefined") {
      localStorage.removeItem(TOUR_STORAGE_KEY);
    }
  }, []);

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { status, type, action } = data;

      if (status === STATUS.FINISHED) {
        endTour();
        return;
      }

      if (status === STATUS.SKIPPED) {
        skipTour();
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        if (action === "prev") {
          setStepIndex((prev) => Math.max(0, prev - 1));
        } else {
          setStepIndex((prev) => Math.min(TOUR_STEPS.length - 1, prev + 1));
        }
      }
    },
    [endTour, skipTour]
  );

  // Keep tour active even when navigating between routes
  // Joyride will handle showing/hiding tooltips based on target availability
  useEffect(() => {
    if (!currentStep) return;
    
    // Only reactivate tour if it was started and we're on a matching route
    if (hasStarted && !isTourActive && isRouteMatch && !isTourSkipped) {
      setIsTourActive(true);
    }
  }, [currentStep, isTourActive, isRouteMatch, hasStarted, isTourSkipped]);

  useEffect(() => {
    if (stepIndex >= TOUR_STEPS.length) {
      endTour();
    }
  }, [endTour, stepIndex]);

  const contextValue: TourContextValue = useMemo(
    () => ({ isTourActive, isTourSkipped, startTour, endTour, skipTour, resetTour }),
    [isTourActive, isTourSkipped, startTour, endTour, skipTour, resetTour]
  );

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      <Joyride
        steps={TOUR_STEPS}
        run={isTourActive}
        stepIndex={stepIndex}
        callback={handleCallback}
        continuous
        showSkipButton
        disableOverlayClose
        scrollToFirstStep
      />
    </TourContext.Provider>
  );
}

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
};
